import { Users, Heart, Scale, Shield, CheckCircle, AlertTriangle } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const Guardianship = () => {
  const mainContent = (
    <>
      {/* Overview Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Guardianship Services</h2>
            <p className="text-lg text-muted-foreground mb-8">
              When a person cannot make decisions for themselves—whether due to age, disability, or incapacity—establishing a legal guardianship protects their interests and ensures proper care. We guide families through this important process with compassion and expertise.
            </p>

            <div className="bg-accent/10 border-l-4 border-accent p-6 rounded-r-lg mb-12">
              <p className="text-lg font-semibold mb-2">
                <AlertTriangle className="inline h-5 w-5 mr-2 text-accent" />
                Important to Know
              </p>
              <p className="text-muted-foreground">
                Guardianship is a serious legal responsibility that removes certain rights from the individual. We explore less restrictive alternatives first whenever possible.
              </p>
            </div>

            <h3 className="text-3xl font-bold mb-8">Types of Guardianship</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Heart className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Guardianship of a Minor</h3>
                <p className="text-muted-foreground mb-4">
                  Legal authority to care for a child when parents cannot fulfill their duties.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Permanent or temporary guardianship</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Guardianship of the person (physical care)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Guardianship of the estate (financial)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Ends when child reaches age 18</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Users className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Guardianship of an Incapacitated Adult</h3>
                <p className="text-muted-foreground mb-4">
                  Legal authority to make decisions for an adult who cannot care for themselves.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Full or limited guardianship available</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Medical evidence of incapacity required</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Guardian of person and/or estate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Court oversight and annual reporting</span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">When is Guardianship Necessary?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="bg-card p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-4 text-accent">For Minors</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Parents deceased or unable to care for child</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Parental rights terminated</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Child has significant inheritance or assets</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Parents are incapacitated or incarcerated</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-4 text-accent">For Adults</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Alzheimer's or dementia</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Traumatic brain injury</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Severe intellectual disability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Mental illness preventing self-care</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Physical incapacity</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guardianship Process */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">The Guardianship Process in Texas</h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "File Application",
                  desc: "Submit guardianship application to the court in the county where the proposed ward resides."
                },
                {
                  step: "2",
                  title: "Medical Certification",
                  desc: "Obtain physician's certificate of incapacity (for adult guardianship) within specific timeframes."
                },
                {
                  step: "3",
                  title: "Citation & Notice",
                  desc: "Serve legal notice to the proposed ward, relatives, and interested parties."
                },
                {
                  step: "4",
                  title: "Court Investigation",
                  desc: "Court investigator or guardian ad litem appointed to investigate and report findings."
                },
                {
                  step: "5",
                  title: "Court Hearing",
                  desc: "Attend hearing where evidence is presented and judge determines if guardianship is appropriate."
                },
                {
                  step: "6",
                  title: "Appointment & Bond",
                  desc: "Guardian appointed, takes oath, and posts bond (if required). Letters of guardianship issued."
                },
                {
                  step: "7",
                  title: "Ongoing Duties",
                  desc: "File annual accountings, reports, and comply with court supervision requirements."
                }
              ].map((item) => (
                <div key={item.step} className="flex gap-4 items-start bg-card p-6 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-gold-shine text-primary font-bold flex items-center justify-center">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-1">{item.title}</h4>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Alternatives to Guardianship */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Alternatives to Guardianship</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Guardianship is a last resort. We explore less restrictive alternatives that preserve individual autonomy:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Scale className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Power of Attorney</h3>
                <p className="text-muted-foreground">
                  Allows someone to manage finances and legal matters without court involvement (must be executed while person has capacity).
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Heart className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Medical Power of Attorney</h3>
                <p className="text-muted-foreground">
                  Designates healthcare decision-maker without full guardianship (must be executed while person has capacity).
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Shield className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Representative Payee</h3>
                <p className="text-muted-foreground">
                  Social Security Administration can appoint someone to manage SSI/SSDI benefits without full guardianship.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Users className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Supported Decision-Making</h3>
                <p className="text-muted-foreground">
                  Less restrictive approach where supporters help the individual make their own decisions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guardian Responsibilities */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Guardian Responsibilities</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-card p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4 text-accent">Guardian of the Person</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Provide food, shelter, and clothing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Ensure proper medical care</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Make educational decisions (for minors)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>File annual personal status reports</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4 text-accent">Guardian of the Estate</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Manage ward's assets and finances</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Pay bills and manage property</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>File annual accounting with court</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Maintain detailed financial records</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );

  return (
    <GenericPracticePage
      title="Guardianship"
      subtitle="Protecting Those Who Cannot Protect Themselves Through Compassionate Legal Guardianship"
      icon={<Users className="h-16 w-16" />}
      mainContent={mainContent}
    />
  );
};

export default Guardianship;